import React, { Component } from "react";
import api from "../utils/api";
import { toast } from "react-toastify";

const formatDateOnly = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

class AdminSpentReportPage extends Component {
  state = {
    sources: [],
    campaigns: [],
    spendData: [],
    leadsData: [],

    expandedSourceId: null,
    startDate: "",
    endDate: "",

    loading: false,
    dataFetched: false,

    sortConfig: { key: "", direction: "asc" },
    filterConfig: {},
  };

  handleSort = (key) => {
    let direction = 'asc';
    if (this.state.sortConfig.key === key && this.state.sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    this.setState({ sortConfig: { key, direction } });
  };

  getSortIcon = (key) => {
    if (this.state.sortConfig.key !== key) return <i className="fas fa-sort text-muted" style={{marginLeft: "5px", opacity: 0.3}}></i>;
    if (this.state.sortConfig.direction === 'asc') return <i className="fas fa-sort-up" style={{marginLeft: "5px", color: "#337ab7"}}></i>;
    return <i className="fas fa-sort-down" style={{marginLeft: "5px", color: "#337ab7"}}></i>;
  };

  handleFilterChange = (key, value) => {
    this.setState(prev => ({
      filterConfig: { ...prev.filterConfig, [key]: value }
    }));
  };

  componentDidMount() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const d = formatDateOnly(yesterday);
    this.setState(
      { startDate: d, endDate: d },
      () => this.fetchSources().then(this.fetchData)
    );
  }

  fetchSources = async () => {
    try {
      const res = await api.get("/admin/settings/sources");
      if (res.data.success) {
        this.setState({ sources: res.data.data || [] });
      }
    } catch (err) {
      toast.error("Error loading sources");
    }
  };

  validateDateRange = () => {
    const { startDate, endDate } = this.state;
    if (!startDate || !endDate) return "Please select both start and end dates.";
    const s = new Date(startDate);
    const e = new Date(endDate);
    if (e < s) return "End date must be after start date.";
    const diffDays = (e - s) / (1000 * 60 * 60 * 24);
    if (diffDays > 62) return "Date range cannot exceed 2 months.";
    return null;
  };

  fetchData = async () => {
    const { startDate, endDate } = this.state;
    const err = this.validateDateRange();
    if (err) { toast.error(err); return; }

    this.setState({ loading: true });
    try {
      const res = await api.get(
        `/admin/marketing/report?start_date=${startDate}&end_date=${endDate}`
      );
      if (res.data.success) {
        const sortedCampaigns = (res.data.data.campaigns || []).sort((a, b) =>
          (a.name || "").localeCompare(b.name || "", undefined, { numeric: true, sensitivity: 'base' })
        );
        this.setState({
          campaigns: sortedCampaigns,
          spendData: res.data.data.spendData,
          leadsData: res.data.data.leadsData,
          dataFetched: true,
          expandedSourceId: null,
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.message || "Error fetching report data");
    } finally {
      this.setState({ loading: false });
    }
  };

  toggleSource = (sourceId) => {
    this.setState((prev) => ({
      expandedSourceId: prev.expandedSourceId === sourceId ? null : sourceId,
    }));
  };

  getCampaignSummary = (campaignId) => {
    const { spendData, leadsData } = this.state;

    let budget = 0;
    let spent = 0;
    let totalLeads = 0;
    let postedLeads = 0;

    spendData.forEach((s) => {
      if (s.campaign_id === campaignId) {
        budget += parseFloat(s.budget || 0);
        spent += parseFloat(s.spent || 0);
      }
    });

    leadsData.forEach((l) => {
      if (l.campaign_id === campaignId) {
        totalLeads += parseInt(l.total_leads || 0, 10);
        postedLeads += parseInt(l.posted_leads || 0, 10);
      }
    });

    const pendingLeads = totalLeads - postedLeads;
    const budgetRemaining = budget - spent;
    const cplPosted = postedLeads > 0 ? spent / postedLeads : null;
    const overallLeadCost = totalLeads > 0 ? spent / totalLeads : null;
    const conversionRatio = totalLeads > 0 ? (postedLeads / totalLeads) * 100 : null;
    const budgetUtilization = budget > 0 ? (spent / budget) * 100 : null;

    return {
      budget,
      spent,
      budgetRemaining,
      totalLeads,
      postedLeads,
      pendingLeads,
      cplPosted,
      overallLeadCost,
      conversionRatio,
      budgetUtilization,
    };
  };

  getSourceSummary = (sourceId) => {
    const { spendData, leadsData } = this.state;

    let spent = 0;
    let totalLeads = 0;
    let postedLeads = 0;

    spendData.forEach((s) => {
      if (String(s.source_id) === String(sourceId)) {
        spent += parseFloat(s.spent || 0);
      }
    });

    leadsData.forEach((l) => {
      if (String(l.source_id) === String(sourceId)) {
        totalLeads += parseInt(l.total_leads || 0, 10);
        postedLeads += parseInt(l.posted_leads || 0, 10);
      }
    });

    const pendingLeads = totalLeads - postedLeads;
    const cplPosted = postedLeads > 0 ? spent / postedLeads : null;
    const overallLeadCost = totalLeads > 0 ? spent / totalLeads : null;
    const conversionRatio = totalLeads > 0 ? (postedLeads / totalLeads) * 100 : null;

    return { spent, totalLeads, postedLeads, pendingLeads, cplPosted, overallLeadCost, conversionRatio };
  };

  fmt = (n) => (n == null ? "—" : `₹ ${n.toFixed(2)}`);
  fmtPct = (n) => (n == null ? "—" : `${n.toFixed(1)}%`);

  renderCampaignDetail(sourceId) {
    const { campaigns } = this.state;
    const { filterConfig, sortConfig } = this.state;

    const filteredCampaigns = campaigns.filter(
      (c) => String(c.source_id) === String(sourceId)
    );

    let totBudget = 0,
      totSpent = 0,
      totBudgetRemaining = 0,
      totTotalLeads = 0,
      totPostedLeads = 0,
      totPendingLeads = 0;

    let rows = filteredCampaigns.map((c) => {
      const s = this.getCampaignSummary(c.id);
      totBudget += s.budget;
      totSpent += s.spent;
      totBudgetRemaining += s.budgetRemaining;
      totTotalLeads += s.totalLeads;
      totPostedLeads += s.postedLeads;
      totPendingLeads += s.pendingLeads;
      return { ...c, ...s };
    });

    Object.keys(filterConfig).forEach(key => {
      const filterVal = filterConfig[key];
      if (filterVal) {
        rows = rows.filter(r => {
          const val = r[key];
          if (val == null) return false;
          return String(val).toLowerCase().includes(filterVal.toLowerCase());
        });
      }
    });

    if (sortConfig.key) {
      const { key, direction } = sortConfig;
      rows.sort((a, b) => {
        let aVal = a[key];
        let bVal = b[key];
        if (key === 'name') {
           const res = (aVal || "").localeCompare(bVal || "", undefined, { numeric: true, sensitivity: 'base' });
           return direction === 'asc' ? res : -res;
        }
        aVal = aVal == null ? 0 : parseFloat(aVal) || 0;
        bVal = bVal == null ? 0 : parseFloat(bVal) || 0;
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    const totCplPosted = totPostedLeads > 0 ? totSpent / totPostedLeads : null;
    const totOverallLeadCost = totTotalLeads > 0 ? totSpent / totTotalLeads : null;
    const totConversionRatio = totTotalLeads > 0 ? (totPostedLeads / totTotalLeads) * 100 : null;
    const totBudgetUtil = totBudget > 0 ? (totSpent / totBudget) * 100 : null;

    const hasRows = rows.length > 0;
    const hasAnyData = rows.some(
      (r) => r.budget > 0 || r.spent > 0 || r.totalLeads > 0 || r.postedLeads > 0
    );

    const utilColor = (pct) => {
      if (pct == null) return "#adb5bd";
      if (pct > 100) return "#dc3545";
      if (pct >= 80) return "#fd7e14";
      if (pct >= 50) return "#ffc107";
      return "#28a745";
    };

    const cplBadgeColor = (cpl) => {
      if (cpl == null) return "#adb5bd";
      if (cpl > 500) return "#dc3545";
      if (cpl > 200) return "#fd7e14";
      return "#28a745";
    };

    const convColor = (pct) => {
      if (pct == null) return "#adb5bd";
      if (pct >= 60) return "#28a745";
      if (pct >= 30) return "#ffc107";
      return "#dc3545";
    };

    return (
      <div style={{ padding: "0 0 18px" }}>
        {/* KPI Strip */}
        {hasRows && hasAnyData && (
          <div className="sr-summary-strip" style={{ padding: "0 18px", marginTop: 14 }}>
            <div className="sr-kpi">
              <div className="val" style={{ color: "#337ab7" }}>₹{totBudget.toFixed(0)}</div>
              <div className="lbl">Total Budget</div>
            </div>
            <div className="sr-kpi">
              <div className="val" style={{ color: "#dc3545" }}>₹{totSpent.toFixed(0)}</div>
              <div className="lbl">Total Spent</div>
            </div>
            <div className="sr-kpi">
              <div className="val" style={{ color: totBudgetRemaining < 0 ? "#dc3545" : "#28a745" }}>
                ₹{totBudgetRemaining.toFixed(0)}
              </div>
              <div className="lbl">Budget Left</div>
            </div>
            <div className="sr-kpi">
              <div className="val">{totTotalLeads}</div>
              <div className="lbl">Total Leads</div>
            </div>
            <div className="sr-kpi">
              <div className="val" style={{ color: "#28a745" }}>{totPostedLeads}</div>
              <div className="lbl">Posted Leads</div>
            </div>
            <div className="sr-kpi">
              <div className="val" style={{ color: "#fd7e14" }}>{totPendingLeads}</div>
              <div className="lbl">Pending Leads</div>
            </div>
            <div className="sr-kpi">
              <div className="val" style={{ color: convColor(totConversionRatio) }}>
                {this.fmtPct(totConversionRatio)}
              </div>
              <div className="lbl">Conversion Rate</div>
            </div>
            <div className="sr-kpi">
              <div className="val" style={{ color: cplBadgeColor(totCplPosted) }}>
                {this.fmt(totCplPosted)}
              </div>
              <div className="lbl">Avg CPL (Posted)</div>
            </div>
          </div>
        )}

        {/* Campaign Table */}
        {!hasRows ? (
          <div className="sr-empty">
            <i className="fas fa-inbox"></i>
            No campaigns found for this source.
          </div>
        ) : !hasAnyData ? (
          <div className="sr-empty">
            <i className="fas fa-chart-bar"></i>
            No data recorded for this source in the selected date range.
          </div>
        ) : (
          <div className="sr-table-wrap" style={{ margin: "0 18px", width: "auto" }}>
            <table className="sr-table">
              <thead>
                <tr>
                  {[
                    { key: "name", label: "Campaign", width: "190px", align: "left" },
                    { key: "budget", label: "Budget (₹)" },
                    { key: "spent", label: "Spent (₹)" },
                    { key: "budgetRemaining", label: "Budget Left" },
                    { key: "budgetUtilization", label: "Budget Util %" },
                    { key: "totalLeads", label: "Total Leads" },
                    { key: "postedLeads", label: "Posted Leads" },
                    { key: "pendingLeads", label: "Pending Leads" },
                    { key: "cplPosted", label: "CPL — Posted" },
                    { key: "overallLeadCost", label: "Overall CPL" },
                    { key: "conversionRatio", label: "Conversion %" },
                  ].map(h => (
                    <th key={h.key} style={{ minWidth: h.width || "auto", textAlign: h.align || "center", verticalAlign: "top" }}>
                      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                        <div
                          onClick={() => this.handleSort(h.key)}
                          style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: h.align === "left" ? "flex-start" : "center", userSelect: "none" }}
                          title={`Sort by ${h.label}`}
                        >
                          {h.label} {this.getSortIcon(h.key)}
                        </div>
                        <input
                          type="text"
                          placeholder="Filter..."
                          value={this.state.filterConfig[h.key] || ""}
                          onChange={(e) => this.handleFilterChange(h.key, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: "11px",
                            padding: "4px 6px",
                            border: "1px solid #dee2e6",
                            borderRadius: "4px",
                            width: "100%",
                            boxSizing: "border-box",
                            fontWeight: "normal",
                            color: "#495057",
                            background: "#fff"
                          }}
                        />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <span
                        style={{
                          display: "inline-block",
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: r.totalLeads > 0 ? "#28a745" : "#dee2e6",
                          marginRight: "8px",
                        }}
                      ></span>
                      {r.name}
                    </td>

                    <td style={{ color: "#337ab7", fontWeight: 600 }}>
                      {r.budget > 0 ? `₹ ${r.budget.toFixed(2)}` : "—"}
                    </td>

                    <td style={{ color: r.spent > 0 ? "#dc3545" : "#adb5bd", fontWeight: 600 }}>
                      {r.spent > 0 ? `₹ ${r.spent.toFixed(2)}` : "—"}
                    </td>

                    <td
                      style={{
                        color:
                          r.budget > 0
                            ? r.budgetRemaining < 0
                              ? "#dc3545"
                              : "#28a745"
                            : "#adb5bd",
                      }}
                    >
                      {r.budget > 0 ? `₹ ${r.budgetRemaining.toFixed(2)}` : "—"}
                    </td>

                    <td>
                      {r.budgetUtilization != null ? (
                        <>
                          <div className="sr-util-bar-wrap">
                            <div
                              className="sr-util-bar"
                              style={{
                                width: `${Math.min(r.budgetUtilization, 100)}%`,
                                background: utilColor(r.budgetUtilization),
                              }}
                            ></div>
                          </div>
                          <span
                            style={{
                              fontSize: "12px",
                              fontWeight: 600,
                              color: utilColor(r.budgetUtilization),
                            }}
                          >
                            {r.budgetUtilization.toFixed(1)}%
                          </span>
                        </>
                      ) : "—"}
                    </td>

                    <td style={{ fontWeight: 600 }}>
                      {r.totalLeads > 0 ? r.totalLeads : "—"}
                    </td>

                    <td>
                      {r.postedLeads > 0 ? (
                        <span className="sr-badge" style={{ background: "#28a745" }}>
                          {r.postedLeads}
                        </span>
                      ) : "—"}
                    </td>

                    <td>
                      {r.pendingLeads > 0 ? (
                        <span className="sr-badge" style={{ background: "#fd7e14" }}>
                          {r.pendingLeads}
                        </span>
                      ) : r.totalLeads > 0 ? (
                        <span style={{ color: "#28a745", fontWeight: 600 }}>0</span>
                      ) : "—"}
                    </td>

                    <td>
                      {r.cplPosted != null ? (
                        <span
                          className="sr-badge"
                          style={{ background: cplBadgeColor(r.cplPosted) }}
                        >
                          ₹ {r.cplPosted.toFixed(2)}
                        </span>
                      ) : r.spent > 0 ? (
                        <span style={{ color: "#adb5bd", fontSize: "12px" }}>No posts</span>
                      ) : "—"}
                    </td>

                    <td style={{ color: "#6c757d" }}>
                      {r.overallLeadCost != null
                        ? `₹ ${r.overallLeadCost.toFixed(2)}`
                        : "—"}
                    </td>

                    <td>
                      {r.conversionRatio != null ? (
                        <span
                          style={{
                            fontWeight: 700,
                            color: convColor(r.conversionRatio),
                          }}
                        >
                          {r.conversionRatio.toFixed(1)}%
                        </span>
                      ) : "—"}
                    </td>
                  </tr>
                ))}

                {/* Totals row */}
                <tr className="sr-total-row">
                  <td>Σ TOTAL</td>

                  <td style={{ color: "#337ab7" }}>
                    {totBudget > 0 ? `₹ ${totBudget.toFixed(2)}` : "—"}
                  </td>

                  <td style={{ color: totSpent > 0 ? "#dc3545" : "#adb5bd" }}>
                    {totSpent > 0 ? `₹ ${totSpent.toFixed(2)}` : "—"}
                  </td>

                  <td
                    style={{
                      color:
                        totBudget > 0
                          ? totBudgetRemaining < 0
                            ? "#dc3545"
                            : "#28a745"
                          : "#adb5bd",
                    }}
                  >
                    {totBudget > 0 ? `₹ ${totBudgetRemaining.toFixed(2)}` : "—"}
                  </td>

                  <td>
                    {totBudgetUtil != null ? (
                      <>
                        <div className="sr-util-bar-wrap">
                          <div
                            className="sr-util-bar"
                            style={{
                              width: `${Math.min(totBudgetUtil, 100)}%`,
                              background: utilColor(totBudgetUtil),
                            }}
                          ></div>
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: 700,
                            color: utilColor(totBudgetUtil),
                          }}
                        >
                          {totBudgetUtil.toFixed(1)}%
                        </span>
                      </>
                    ) : "—"}
                  </td>

                  <td>{totTotalLeads > 0 ? totTotalLeads : "—"}</td>

                  <td>
                    {totPostedLeads > 0 ? (
                      <span className="sr-badge" style={{ background: "#28a745" }}>
                        {totPostedLeads}
                      </span>
                    ) : "—"}
                  </td>

                  <td>
                    {totPendingLeads > 0 ? (
                      <span className="sr-badge" style={{ background: "#fd7e14" }}>
                        {totPendingLeads}
                      </span>
                    ) : totTotalLeads > 0 ? (
                      <span style={{ color: "#28a745" }}>0</span>
                    ) : "—"}
                  </td>

                  <td>
                    {totCplPosted != null ? (
                      <span
                        className="sr-badge"
                        style={{ background: cplBadgeColor(totCplPosted) }}
                      >
                        ₹ {totCplPosted.toFixed(2)}
                      </span>
                    ) : "—"}
                  </td>

                  <td style={{ color: "#6c757d" }}>
                    {totOverallLeadCost != null
                      ? `₹ ${totOverallLeadCost.toFixed(2)}`
                      : "—"}
                  </td>

                  <td>
                    {totConversionRatio != null ? (
                      <span
                        style={{
                          fontWeight: 700,
                          color: convColor(totConversionRatio),
                        }}
                      >
                        {totConversionRatio.toFixed(1)}%
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Legend */}
        {hasRows && hasAnyData && (
          <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginTop: "14px", padding: "0 18px" }}>
            <span style={{ fontSize: "11px", color: "#6c757d" }}>
              <strong>CPL (Posted)</strong> = Spent ÷ Posted Leads
            </span>
            <span style={{ fontSize: "11px", color: "#6c757d" }}>
              <strong>Overall CPL</strong> = Spent ÷ Total Leads
            </span>
            <span style={{ fontSize: "11px", color: "#6c757d" }}>
              <strong>Conversion</strong> = Posted ÷ Total Leads
            </span>
            <span style={{ fontSize: "11px", color: "#6c757d" }}>
              <strong>Budget Util</strong> = Spent ÷ Budget
            </span>
          </div>
        )}
      </div>
    );
  }

  render() {
    const {
      sources,
      startDate,
      endDate,
      loading,
      dataFetched,
      expandedSourceId,
    } = this.state;

    const sourceRows = dataFetched
      ? sources.map((s) => ({ id: s.id, title: s.title, ...this.getSourceSummary(s.id) }))
      : [];

    const cplBadgeColor = (cpl) => {
      if (cpl == null) return "#adb5bd";
      if (cpl > 500) return "#dc3545";
      if (cpl > 200) return "#fd7e14";
      return "#28a745";
    };

    const convColor = (pct) => {
      if (pct == null) return "#adb5bd";
      if (pct >= 60) return "#28a745";
      if (pct >= 30) return "#ffc107";
      return "#dc3545";
    };

    const styles = `
      .sr-card {
        background: #fff;
        border-radius: 14px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.08);
        border: 0;
        margin-bottom: 20px;
      }
      .sr-card-body { padding: 20px 22px; }

      .sr-filter-row { display: flex; align-items: flex-end; gap: 14px; flex-wrap: wrap; }
      .sr-filter-group { display: flex; flex-direction: column; gap: 4px; }
      .sr-filter-group label { font-size: 11px; font-weight: 700; color: #6c757d; text-transform: uppercase; letter-spacing: 0.06em; margin: 0; }
      .sr-filter-group select,
      .sr-filter-group input[type="date"] {
        border: 1px solid #dee2e6;
        border-radius: 8px;
        padding: 8px 12px;
        font-size: 13px;
        background: #f8f9fa;
        color: #212529;
        outline: none;
        min-width: 160px;
        height: 38px;
      }
      .sr-filter-group select:focus,
      .sr-filter-group input[type="date"]:focus {
        border-color: #337ab7;
        background: #fff;
        box-shadow: 0 0 0 3px rgba(51,122,183,0.12);
      }

      .sr-btn-generate {
        padding: 9px 22px;
        background: #337ab7;
        color: #fff;
        border: 0;
        border-radius: 8px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
        white-space: nowrap;
        transition: background 0.15s;
        height: 38px;
      }
      .sr-btn-generate:hover { background: #286090; }
      .sr-btn-generate:disabled { background: #a0bcd8; cursor: default; }

      .sr-summary-strip { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 18px; }
      .sr-kpi {
        background: #f8f9fa;
        border: 1px solid #e9ecef;
        border-radius: 10px;
        padding: 12px 16px;
        flex: 1;
        min-width: 120px;
        text-align: center;
      }
      .sr-kpi .val { font-size: 17px; font-weight: 700; color: #212529; }
      .sr-kpi .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: #6c757d; margin-top: 2px; }

      .sr-table-wrap { overflow-x: auto; overflow-y: auto; max-height: calc(100vh - 420px); min-height: 120px; border-radius: 10px; border: 1px solid #dee2e6; }
      .sr-table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 13px; }
      .sr-table thead th {
        background: #f1f5f9;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.07em;
        font-weight: 700;
        color: #495057;
        padding: 11px 14px;
        border-bottom: 2px solid #dee2e6;
        text-align: center;
        position: sticky;
        top: 0;
        z-index: 2;
      }
      .sr-table thead th:first-child { text-align: left; }
      .sr-table tbody td {
        padding: 10px 14px;
        border-bottom: 1px solid #f0f2f5;
        text-align: center;
        vertical-align: middle;
        color: #343a40;
      }
      .sr-table tbody td:first-child { text-align: left; font-weight: 600; }
      .sr-table tbody tr:hover td { background: #f7faff; }
      .sr-table tbody tr:last-child td { border-bottom: 0; }

      .sr-total-row td {
        background: #f1f5f9 !important;
        font-weight: 700 !important;
        border-top: 2px solid #dee2e6 !important;
        color: #212529 !important;
      }

      .sr-badge {
        display: inline-block;
        padding: 4px 10px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 600;
        color: #fff;
      }

      .sr-util-bar-wrap {
        background: #e9ecef;
        border-radius: 20px;
        height: 6px;
        min-width: 60px;
        overflow: hidden;
        display: inline-block;
        width: 80px;
        vertical-align: middle;
        margin-right: 4px;
      }
      .sr-util-bar { height: 100%; border-radius: 20px; }

      .sr-empty { text-align: center; padding: 40px 20px; color: #adb5bd; }
      .sr-empty i { font-size: 30px; margin-bottom: 10px; display: block; }

      .sr-placeholder { text-align: center; padding: 60px 20px; color: #adb5bd; }
      .sr-placeholder i { font-size: 40px; margin-bottom: 14px; display: block; color: #dee2e6; }

      .sr-source-row {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 14px 18px;
        cursor: pointer;
        border-bottom: 1px solid #f0f2f5;
        transition: background 0.1s;
      }
      .sr-source-row:hover { background: #f7faff; }
      .sr-source-row:last-child { border-bottom: 0; }
      .sr-source-row.expanded { background: #eef5fc; border-bottom: 1px solid #dee2e6; }
      .sr-source-total-row { background: #f1f5f9; border-top: 2px solid #dee2e6; border-bottom: 0; cursor: default; font-weight: 700; }
      .sr-source-total-row:hover { background: #f1f5f9; }
      .sr-source-total-row .sr-source-name { color: #212529; font-weight: 700; }
      .sr-source-name { flex: 1 1 180px; font-weight: 700; color: #212529; display: flex; align-items: center; gap: 8px; min-width: 160px; }
      .sr-source-metric { flex: 1 1 110px; text-align: center; min-width: 100px; }
      .sr-source-metric .val { font-weight: 600; color: #343a40; font-size: 13px; }
      .sr-source-metric .lbl { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #adb5bd; margin-top: 2px; }
      .sr-source-header {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 10px 18px;
        background: #f8f9fa;
        border-bottom: 2px solid #dee2e6;
      }
      .sr-source-header .sr-source-name { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #6c757d; font-weight: 700; }
      .sr-source-header .sr-source-metric .val { font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: #6c757d; font-weight: 700; }
    `;

    return (
      <div style={{ background: "#f1f5f9", padding: "20px", minHeight: "100%" }}>
        <style>{styles}</style>

        {/* Page Header */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ fontWeight: 700, marginTop: 0, marginBottom: "4px", color: "#1a2533" }}>
            <i className="fas fa-chart-bar text-primary" style={{ marginRight: "10px" }}></i>
            Ad Campaign Performance Report
          </h4>
          <p className="text-muted small" style={{ marginBottom: 0 }}>
            Analyse budget, spend, and lead performance of your Meta ad campaigns by source.
          </p>
        </div>

        {/* Filters Card */}
        <div className="sr-card">
          <div className="sr-card-body">
            <div className="sr-filter-row">
              <div className="sr-filter-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => this.setState({ startDate: e.target.value })}
                />
              </div>

              <div className="sr-filter-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => this.setState({ endDate: e.target.value })}
                />
              </div>

              <button
                className="sr-btn-generate"
                onClick={this.fetchData}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{ marginRight: "6px" }}></i>
                    Loading…
                  </>
                ) : (
                  <>
                    <i className="fas fa-play" style={{ marginRight: "6px" }}></i>
                    Generate Report
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Source Summary */}
        {dataFetched ? (
          sourceRows.length === 0 ? (
            <div className="sr-card">
              <div className="sr-empty">
                <i className="fas fa-inbox"></i>
                No active sources found.
              </div>
            </div>
          ) : (
            <div className="sr-card" style={{ padding: 0, overflow: "hidden" }}>
              <div className="sr-source-header">
                <div className="sr-source-name">Source</div>
                <div className="sr-source-metric"><div className="val">Total Spent</div></div>
                <div className="sr-source-metric"><div className="val">Total Leads</div></div>
                <div className="sr-source-metric"><div className="val">Posted Leads</div></div>
                <div className="sr-source-metric"><div className="val">Pending Leads</div></div>
                <div className="sr-source-metric"><div className="val">CPL — Posted</div></div>
                <div className="sr-source-metric"><div className="val">Overall CPL</div></div>
                <div className="sr-source-metric"><div className="val">Conversion %</div></div>
                <div style={{ width: 16 }}></div>
              </div>

              {sourceRows.map((r) => {
                const isExpanded = expandedSourceId === r.id;
                return (
                  <div key={r.id}>
                    <div
                      className={`sr-source-row${isExpanded ? " expanded" : ""}`}
                      onClick={() => this.toggleSource(r.id)}
                    >
                      <div className="sr-source-name">
                        <span
                          style={{
                            display: "inline-block",
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: r.totalLeads > 0 ? "#28a745" : "#dee2e6",
                          }}
                        ></span>
                        {r.title}
                      </div>
                      <div className="sr-source-metric">
                        <div className="val" style={{ color: r.spent > 0 ? "#dc3545" : "#adb5bd" }}>
                          {r.spent > 0 ? `₹ ${r.spent.toFixed(2)}` : "—"}
                        </div>
                      </div>
                      <div className="sr-source-metric">
                        <div className="val">{r.totalLeads > 0 ? r.totalLeads : "—"}</div>
                      </div>
                      <div className="sr-source-metric">
                        <div className="val">
                          {r.postedLeads > 0 ? (
                            <span className="sr-badge" style={{ background: "#28a745" }}>{r.postedLeads}</span>
                          ) : "—"}
                        </div>
                      </div>
                      <div className="sr-source-metric">
                        <div className="val">
                          {r.pendingLeads > 0 ? (
                            <span className="sr-badge" style={{ background: "#fd7e14" }}>{r.pendingLeads}</span>
                          ) : r.totalLeads > 0 ? (
                            <span style={{ color: "#28a745" }}>0</span>
                          ) : "—"}
                        </div>
                      </div>
                      <div className="sr-source-metric">
                        <div className="val">
                          {r.cplPosted != null ? (
                            <span className="sr-badge" style={{ background: cplBadgeColor(r.cplPosted) }}>
                              ₹ {r.cplPosted.toFixed(2)}
                            </span>
                          ) : r.spent > 0 ? (
                            <span style={{ color: "#adb5bd", fontSize: "12px" }}>No posts</span>
                          ) : "—"}
                        </div>
                      </div>
                      <div className="sr-source-metric">
                        <div className="val" style={{ color: "#6c757d", fontWeight: 400 }}>
                          {r.overallLeadCost != null ? `₹ ${r.overallLeadCost.toFixed(2)}` : "—"}
                        </div>
                      </div>
                      <div className="sr-source-metric">
                        <div className="val" style={{ color: convColor(r.conversionRatio), fontWeight: 700 }}>
                          {r.conversionRatio != null ? `${r.conversionRatio.toFixed(1)}%` : "—"}
                        </div>
                      </div>
                      <div style={{ width: 16, textAlign: "center", color: "#adb5bd" }}>
                        <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                      </div>
                    </div>
                    {isExpanded && this.renderCampaignDetail(r.id)}
                  </div>
                );
              })}

              {(() => {
                const totSpent = sourceRows.reduce((a, r) => a + (r.spent || 0), 0);
                const totTotalLeads = sourceRows.reduce((a, r) => a + (r.totalLeads || 0), 0);
                const totPostedLeads = sourceRows.reduce((a, r) => a + (r.postedLeads || 0), 0);
                const totPendingLeads = sourceRows.reduce((a, r) => a + (r.pendingLeads || 0), 0);
                const totCplPosted = totPostedLeads > 0 ? totSpent / totPostedLeads : null;
                const totOverallLeadCost = totTotalLeads > 0 ? totSpent / totTotalLeads : null;
                const totConversionRatio = totTotalLeads > 0 ? (totPostedLeads / totTotalLeads) * 100 : null;

                return (
                  <div className="sr-source-row sr-source-total-row">
                    <div className="sr-source-name">Σ OVERALL TOTAL</div>
                    <div className="sr-source-metric">
                      <div className="val" style={{ color: totSpent > 0 ? "#dc3545" : "#adb5bd" }}>
                        {totSpent > 0 ? `₹ ${totSpent.toFixed(2)}` : "—"}
                      </div>
                    </div>
                    <div className="sr-source-metric">
                      <div className="val">{totTotalLeads > 0 ? totTotalLeads : "—"}</div>
                    </div>
                    <div className="sr-source-metric">
                      <div className="val">
                        {totPostedLeads > 0 ? (
                          <span className="sr-badge" style={{ background: "#28a745" }}>{totPostedLeads}</span>
                        ) : "—"}
                      </div>
                    </div>
                    <div className="sr-source-metric">
                      <div className="val">
                        {totPendingLeads > 0 ? (
                          <span className="sr-badge" style={{ background: "#fd7e14" }}>{totPendingLeads}</span>
                        ) : totTotalLeads > 0 ? (
                          <span style={{ color: "#28a745" }}>0</span>
                        ) : "—"}
                      </div>
                    </div>
                    <div className="sr-source-metric">
                      <div className="val">
                        {totCplPosted != null ? (
                          <span className="sr-badge" style={{ background: cplBadgeColor(totCplPosted) }}>
                            ₹ {totCplPosted.toFixed(2)}
                          </span>
                        ) : "—"}
                      </div>
                    </div>
                    <div className="sr-source-metric">
                      <div className="val" style={{ color: "#6c757d" }}>
                        {totOverallLeadCost != null ? `₹ ${totOverallLeadCost.toFixed(2)}` : "—"}
                      </div>
                    </div>
                    <div className="sr-source-metric">
                      <div className="val" style={{ color: convColor(totConversionRatio) }}>
                        {totConversionRatio != null ? `${totConversionRatio.toFixed(1)}%` : "—"}
                      </div>
                    </div>
                    <div style={{ width: 16 }}></div>
                  </div>
                );
              })()}
            </div>
          )
        ) : !loading ? (
          <div className="sr-card">
            <div className="sr-placeholder">
              <i className="fas fa-filter"></i>
              <p style={{ margin: 0, fontSize: "14px" }}>
                Select a <strong>date range</strong>, then click <strong>Generate Report</strong>.
              </p>
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

export default AdminSpentReportPage;
